import { Box, Text } from "@chakra-ui/react";
import { FC } from "react";

import { Schematisation, SchematisationRevision, ThreediModel } from "@nens/threedi/openapi-client";

import { GridCard } from "../../components/GridCard";
import { useLatestRevisionTask } from "../../queries/schematisations";
import { useModelTasks } from "../../queries/threedimodels";

import { ModelCheckerErrors } from "./ModelCheckerErrors";

export interface RevisionCardProps {
  model: ThreediModel | null;
  schematisation: Schematisation;
  revision: SchematisationRevision;
}

const RevisionCard: FC<RevisionCardProps> = ({ model, schematisation, revision }) => {
  const latestRevisionTask = useLatestRevisionTask(schematisation.id, revision.id);

  const downloadTaskJson = () => {
    if (latestRevisionTask && latestRevisionTask.url) {
      window.open(latestRevisionTask.url, "_blank");
    }
  };

  const taskErrors: any[] = (latestRevisionTask?.detail as any)?.result?.errors || [];

  // Revision with failed tasks for the model is also counted as invalid.
  const modelTasksResponse = useModelTasks(model ? model.id : undefined);
  const failedModelTasksForThisRevision =
    model && modelTasksResponse.isSuccess
      ? modelTasksResponse.data.results.filter((task) => {
          if (task.status === "failure" && !!task.params) {
            const params = task.params as Record<string, string | number>;
            if (
              params.revision_pk &&
              params.revision_pk === model.revisionId &&
              params.schematisation_pk &&
              params.schematisation_pk === model.schematisationId
            ) {
              return true;
            }
          }
          return false;
        })
      : [];

  const downloadFailedModelTasksJson = () => {
    failedModelTasksForThisRevision.forEach((task) => window.open(task.url, "_blank"));
  };

  return (
    <GridCard colSpan={2} title={`Revision #${revision.number} (id ${revision.id})`}>
      {revision.archived ? (
        <Box>
          This revision was <strong>archived</strong> on {revision.archived.toLocaleString()}.
        </Box>
      ) : null}
      <Text mt={1}>
        Committed by {revision.commitFirstName} {revision.commitLastName} on{" "}
        {revision.created?.toLocaleString()}
      </Text>

      {!revision.isValid ? (
        <>
          <Text>
            This revision is <strong>not valid</strong>.
          </Text>
          <ModelCheckerErrors
            errors={taskErrors ? taskErrors.map((error) => error.description) : []}
            downloadTask={
              latestRevisionTask && latestRevisionTask.url ? downloadTaskJson : undefined
            }
          />
        </>
      ) : null}
      {failedModelTasksForThisRevision.length > 0 ? (
        <>
          <Text>
            There are <strong>failed tasks</strong> for this model.
          </Text>
          <ModelCheckerErrors
            errors={
              failedModelTasksForThisRevision
                ? failedModelTasksForThisRevision.map((task) => (task.detail as any).message || "")
                : []
            }
            downloadTask={downloadFailedModelTasksJson}
          />
        </>
      ) : null}
    </GridCard>
  );
};

export default RevisionCard;
